import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Package, Truck, ShieldCheck } from 'lucide-react';
import axios from 'axios';
import Navbar from '@/components/Navbar';
import { Button } from '@/components/ui/button';

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_petshop-seedlings/artifacts/d48ts1t7_civaie40.jpeg';
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function HomePage() {
  const [siteContent, setSiteContent] = useState({
    homepage_title: 'Civaie',
    homepage_subtitle: 'Di Lelmi Luciana',
    homepage_description: 'Scopri la nostra selezione di prodotti per animali domestici e piantine da orto.',
    homepage_order_text: 'Ordina online e scegli consegna o ritiro in negozio.',
    homepage_button_text: 'Inizia ad Acquistare',
    homepage_categories_title: 'Le Nostre Categorie',
    homepage_features_title: 'Perché Sceglierci',
    homepage_feature1_title: 'Prodotti di Qualità',
    homepage_feature1_desc: 'Selezioniamo solo i migliori prodotti per i tuoi animali e il tuo giardino',
    homepage_feature2_title: 'Consegna o Ritiro',
    homepage_feature2_desc: 'Scegli se ritirare in negozio o ricevere la consegna a domicilio',
    homepage_feature3_title: 'Pagamento Sicuro',
    homepage_feature3_desc: 'Paga con PayPal, Apple Pay o ordina senza pagamento online',
    footer_text: 'Civaie - Di Lelmi Luciana',
    footer_subtext: '40 anni di attività'
  });

  useEffect(() => {
    const fetchSiteContent = async () => {
      try {
        const response = await axios.get(`${API}/site-content`);
        setSiteContent(response.data);
      } catch (error) {
        console.error('Failed to fetch site content:', error);
      }
    };
    fetchSiteContent();
  }, []);

  const categories = [
    {
      name: 'Pet Shop',
      slug: 'petshop',
      image: 'https://images.unsplash.com/photo-1648799834307-97650bbf7298?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzd8MHwxfHNlYXJjaHwxfHxoYXBweSUyMGRvZyUyMHBvcnRyYWl0fGVufDB8fHx8MTc2Njg3ODAxOXww&ixlib=rb-4.1.0&q=85',
      color: 'rose'
    },
    {
      name: 'Giardinaggio',
      slug: 'giardinaggio',
      image: 'https://images.unsplash.com/photo-1591857172940-6a994103e3df?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzZ8MHwxfHNlYXJjaHwxfHxwb3R0ZWQlMjB2ZWdldGFibGUlMjBwbGFudHMlMjBnYXJkZW58ZW58MHx8fHwxNzY2ODc4MDIxfDA&ixlib=rb-4.1.0&q=85',
      color: 'emerald'
    },
    {
      name: 'Varie',
      slug: 'varie',
      image: 'https://images.unsplash.com/photo-1724773689174-a2475dcf4ffd?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzd8MHwxfHNlYXJjaHwxfHxnYXJkZW5pbmclMjB0b29scyUyMGFlc3RoZXRpY3xlbnwwfHx8fDE3NjY4NzgwMjN8MA&ixlib=rb-4.1.0&q=85',
      color: 'amber'
    }
  ];

  const features = [
    {
      icon: Package,
      title: 'Prodotti di Qualità',
      description: 'Selezioniamo solo i migliori prodotti per i tuoi animali e il tuo giardino'
    },
    {
      icon: Truck,
      title: 'Consegna o Ritiro',
      description: 'Scegli se ritirare in negozio o ricevere la consegna a domicilio'
    },
    {
      icon: ShieldCheck,
      title: 'Pagamento Sicuro',
      description: 'Paga con PayPal, Apple Pay o ordina senza pagamento online'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      <section className="relative overflow-hidden py-20 md:py-32 bg-gradient-to-br from-orange-50 via-white to-emerald-50">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center max-w-4xl mx-auto"
          >
            <div className="flex justify-center mb-8">
              <img 
                src={LOGO_URL} 
                alt={siteContent.homepage_title || 'Civaie'} 
                className="h-32 w-32 rounded-full shadow-xl object-cover"
              />
            </div>
            <h1 className="text-4xl md:text-6xl font-extrabold tracking-tight mb-6" data-testid="hero-title">
              <span className="text-rose-600">{siteContent.homepage_title || 'Civaie'}</span>
              <br />
              <span className="text-gray-800">{siteContent.homepage_subtitle || 'Di Lelmi Luciana'}</span>
            </h1>
            <p className="text-lg md:text-xl text-gray-600 mb-8 leading-relaxed">
              {siteContent.homepage_description || 'Scopri la nostra selezione di prodotti per animali domestici e piantine da orto.'}
              <br />
              {siteContent.homepage_order_text || 'Ordina online e scegli consegna o ritiro in negozio.'}
            </p>
            <div className="flex gap-4 justify-center flex-wrap">
              <Link to="/categories" data-testid="shop-now-button">
                <Button className="bg-rose-600 hover:bg-rose-700 text-white rounded-full font-bold px-8 py-6 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-0.5">
                  {siteContent.homepage_button_text || 'Inizia ad Acquistare'}
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      <section className="py-16 md:py-24 bg-white">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Categorie</h2>
            <p className="text-lg text-gray-600">Esplora le nostre categorie di prodotti</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {categories.map((category, index) => (
              <motion.div
                key={category.slug}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <Link
                  to={category.slug === 'petshop' ? '/categories?level=petshop' : `/products?category=${category.slug}`}
                  className="group block relative overflow-hidden rounded-2xl aspect-[4/5] cursor-pointer"
                  data-testid={`category-${category.slug}`}
                >
                  <img
                    src={category.image}
                    alt={category.name}
                    className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-110"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
                  <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
                    <h3 className="text-2xl font-bold mb-2">{category.name}</h3>
                    <div className="flex items-center text-sm font-semibold">
                      Esplora <ArrowRight className="ml-1 h-4 w-4" />
                    </div>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 md:py-24 bg-orange-50">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Perché Sceglierci</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="text-center"
              >
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-white shadow-lg mb-4">
                  <feature.icon className="h-8 w-8 text-rose-600" />
                </div>
                <h3 className="text-xl font-bold mb-2">{feature.title}</h3>
                <p className="text-gray-600">{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <footer className="bg-gray-900 text-white py-12">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl text-center">
          <p className="text-lg font-semibold mb-2">Civaie - Di Lelmi Luciana</p>
          <p className="text-gray-400 text-sm">40 anni di attività</p>
          <p className="text-gray-400 text-sm mt-2">© 2025 Tutti i diritti riservati</p>
        </div>
      </footer>
    </div>
  );
}